import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Axios from "../../utils/axios";
import { authHeaders } from "../../utils/axios";
import { toast } from "../../context/TostContext";

// Listings
export const fetchMarketplaceListings = createAsyncThunk(
    "marketplace/fetchListings",
    async (params, { rejectWithValue }) => {
        try {
            const { page, limit, search, status, seller_type, seller_id, category_id, city_id, state_id, min_price, max_price, start_date, end_date } = params;

            const queryParams = new URLSearchParams({
                page: page || 1,
                limit: limit || 10,
                search: search || ""
            });

            if (status) queryParams.append("status", status);
            if (seller_type) queryParams.append("seller_type", seller_type);
            if (seller_id) queryParams.append("seller_id", seller_id);
            if (start_date) queryParams.append("start_date", start_date);
            if (end_date) queryParams.append("end_date", end_date);
            if (min_price) queryParams.append("min_price", min_price);
            if (max_price) queryParams.append("max_price", max_price);

            // Handle potential arrays for multi-select
            const appendMulti = (key, value) => {
                if (Array.isArray(value)) {
                    value.forEach(v => queryParams.append(key, v));
                } else if (value) {
                    queryParams.append(key, value);
                }
            };

            appendMulti("category_id", category_id);
            appendMulti("city_id", city_id);
            appendMulti("state_id", state_id);

            const typePath = seller_type === 'company' ? 'company-offer' : 'user-listing';
            const response = await Axios.get(`/marketplace/${typePath}?${queryParams.toString()}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchMarketplaceStats = createAsyncThunk(
    "marketplace/fetchStats",
    async (params, { rejectWithValue }) => {
        try {
            const seller_type = params?.seller_type || 'user';
            const url = seller_type === 'company' ? "/marketplace/company-offer/stats" : "/marketplace/user-listing/stats";
            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchListingDetails = createAsyncThunk(
    "marketplace/fetchDetails",
    async ({ id, seller_type }, { rejectWithValue }) => {
        try {
            const typePath = seller_type === 'company' ? 'company-offer' : 'user-listing';
            const response = await Axios.get(`/marketplace/${typePath}/${id}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const moderateListing = createAsyncThunk(
    "marketplace/moderate",
    async ({ id, seller_type, status, rejection_reason }, { rejectWithValue }) => {
        try {
            const typePath = seller_type === 'company' ? 'company-offer' : 'user-listing';
            const response = await Axios.patch(`/marketplace/${typePath}/${id}/moderate`, { status, rejection_reason }, authHeaders());
            toast.success(response.data.message);
            return { id, status };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const bulkModerateListings = createAsyncThunk(
    "marketplace/bulkModerate",
    async ({ ids, status, rejection_reason }, { rejectWithValue }) => {
        try {
            const response = await Axios.post(`/marketplace/bulk-moderate`, { ids, status, rejection_reason }, authHeaders());
            toast.success(response.data.message);
            return { ids, status };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const createBulkOffer = createAsyncThunk(
    "marketplace/createBulkOffer",
    async (data, { rejectWithValue }) => {
        try {
            const response = await Axios.post(`/marketplace/company-offer`, data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateBulkOffer = createAsyncThunk(
    "marketplace/updateBulkOffer",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await Axios.put(`/marketplace/company-offer/${id}`, data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const createUserListing = createAsyncThunk(
    "marketplace/createUserListing",
    async (data, { rejectWithValue }) => {
        try {
            const response = await Axios.post(`/marketplace/user-listing`, data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateUserListing = createAsyncThunk(
    "marketplace/updateUserListing",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await Axios.patch(`/marketplace/user-listing/${id}`, data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteUserListing = createAsyncThunk(
    "marketplace/deleteUserListing",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`/marketplace/user-listing/${id}`, authHeaders());
            toast.success(response.data.message);
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Media Upload
export const uploadMarketplaceMedia = createAsyncThunk(
    "marketplace/uploadMedia",
    async ({ company_id, user_id, files, collection = "main" }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            if (company_id) formData.append("company_id", company_id);
            if (user_id) formData.append("user_id", user_id);
            formData.append("collection", collection);
            files.forEach(file => {
                formData.append("files", file);
            });

            const response = await Axios.post(`/marketplace/media/upload`, formData, {
                headers: {
                    ...authHeaders().headers,
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteMarketplaceMedia = createAsyncThunk(
    "marketplace/deleteMedia",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`/marketplace/media/${id}`, authHeaders());
            toast.success(response.data.message);
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Categories
export const fetchMarketplaceCategories = createAsyncThunk(
    "marketplace/fetchCategories",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get("/marketplace/categories", authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchMarketplaceCategoriesPaginated = createAsyncThunk(
    "marketplace/fetchCategoriesPaginated",
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`/marketplace/categories?search=${search || ""}&page=${page || 1}&limit=${limit || 10}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchMarketplaceConfig = createAsyncThunk(
    "marketplace/fetchConfig",
    async (_, { rejectWithValue }) => {
        // console.log("fetchMarketplaceConfig --=-----=-==-===--");
        try {
            const response = await Axios.get("/marketplace/config", authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const createMarketplaceCategory = createAsyncThunk(
    "marketplace/createCategory",
    async (data, { rejectWithValue }) => {
        try {
            const response = await Axios.post("/marketplace/categories", data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateMarketplaceCategory = createAsyncThunk(
    "marketplace/updateCategory",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await Axios.put(`/marketplace/categories/${id}`, data, authHeaders());
            toast.success(response.data.message);
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteMarketplaceCategory = createAsyncThunk(
    "marketplace/deleteCategory",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`/marketplace/categories/${id}`, authHeaders());
            toast.success(response.data.message);
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Bulk Offer Requests
export const fetchBulkOfferRequests = createAsyncThunk(
    "marketplace/fetchBulkOfferRequests",
    async ({ page, limit, search, status }, { rejectWithValue }) => {
        try {
            let url = `/marketplace/bulk-offer-requests?page=${page || 1}&limit=${limit || 10}&search=${search || ""}`;
            if (status) url += `&status=${status}`;

            const response = await Axios.get(url, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch bulk offer requests");
        }
    }
);

export const fetchAdminMarketplaceBookings = createAsyncThunk(
    "marketplace/fetchAdminBookings",
    async (params, { rejectWithValue }) => {
        try {
            const tempObj = { ...params };
            const queryParams = Object.keys(tempObj)
                .filter(key => tempObj[key] !== undefined && tempObj[key] !== null && tempObj[key] !== '')
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(tempObj[key])}`)
                .join('&');
            const response = await Axios.get(`/marketplace/bookings?${queryParams}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch admin bookings");
        }
    }
);

export const fetchAdminBookingDetails = createAsyncThunk(
    "marketplace/fetchAdminBookingDetails",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`/marketplace/bookings/${id}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch booking details");
        }
    }
);

export const fetchAdminBookingStats = createAsyncThunk(
    "marketplace/fetchAdminBookingStats",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get("/marketplace/bookings/stats", authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch booking statistics");
        }
    }
);

export const exportAdminMarketplaceBookings = createAsyncThunk(
    "marketplace/exportAdminMarketplaceBookings",
    async (params, { rejectWithValue }) => {
        try {
            const response = await Axios.get("/marketplace/bookings/export", {
                ...authHeaders(),
                params,
                responseType: 'blob'
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Marketplace_Bookings_${new Date().getTime()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            return { success: true };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to export bookings");
        }
    }
);

export const fetchBulkOfferRequestStats = createAsyncThunk(
    "marketplace/fetchBulkOfferRequestStats",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get("/marketplace/bulk-offer-requests/stats", authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteBulkOfferRequest = createAsyncThunk(
    "marketplace/deleteBulkOfferRequest",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`/marketplace/bulk-offer-requests/${id}`, authHeaders());
            toast.success(response.data.message);
            return id;
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateBulkOfferRequestStatus = createAsyncThunk(
    "marketplace/updateBulkOfferRequestStatus",
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const response = await Axios.patch(`/marketplace/bulk-offer-requests/${id}/status`, { status }, authHeaders());
            toast.success(response.data.message);
            return { id, status };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const toggleCategoryStatus = createAsyncThunk(
    "marketplace/toggleCategoryStatus",
    async ({ id, is_active }, { rejectWithValue }) => {
        try {
            const response = await Axios.patch(`/marketplace/categories/${id}/toggle-status`, { is_active }, authHeaders());
            toast.success(response.data.message);
            return { id, is_active };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const toggleListingActiveStatus = createAsyncThunk(
    "marketplace/toggleActiveStatus",
    async ({ id, is_active }, { rejectWithValue }) => {
        try {
            const response = await Axios.patch(`/marketplace/company-offer/${id}/toggle-status`, { is_active }, authHeaders());
            toast.success(response.data.message);
            return { id, is_active };
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


export const fetchMarketplaceRequirements = createAsyncThunk(
    "marketplace/fetchRequirements",
    async (params, { rejectWithValue }) => {
        try {
            const { page, limit, search, status, category_id, city_id, state_id } = params || {};
            const queryParams = new URLSearchParams({
                page: page || 1,
                limit: limit || 10,
                search: search || ""
            });

            if (status) queryParams.append("status", status);
            if (category_id) queryParams.append("category_id", category_id);
            if (city_id) queryParams.append("city_id", city_id);
            if (state_id) queryParams.append("state_id", state_id);

            const response = await Axios.get(`/marketplace/requirements?${queryParams.toString()}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchMarketplaceRequirementDetails = createAsyncThunk(
    "marketplace/fetchRequirementDetails",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.get(`/marketplace/requirements/${id}`, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const createMarketplaceRequirement = createAsyncThunk(
    "marketplace/createRequirement",
    async (data, { rejectWithValue }) => {
        try {
            const response = await Axios.post(`/marketplace/requirements`, data, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateMarketplaceRequirement = createAsyncThunk(
    "marketplace/updateRequirement",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await Axios.patch(`/marketplace/requirements/${id}`, data, authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const deleteMarketplaceRequirement = createAsyncThunk(
    "marketplace/deleteRequirement",
    async (id, { rejectWithValue }) => {
        try {
            const response = await Axios.delete(`/marketplace/requirements/${id}`, authHeaders());
            return { id, message: response.data.message };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchMarketplaceRequirementStats = createAsyncThunk(
    "marketplace/fetchRequirementStats",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get("/marketplace/requirements/stats", authHeaders());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

const marketplaceSlice = createSlice({
    name: "marketplace",
    initialState: {
        listings: [],
        pagination: {},
        stats: null,
        details: null,
        categories: [],
        bulkOfferRequests: [],
        bulkOfferRequestPagination: { total: 0, page: 1, limit: 10, pages: 0 },
        bulkOfferRequestStats: null,
        // Admin Bookings
        adminMarketplaceBookings: [],
        adminMarketplaceBookingPagination: { total: 0, page: 1, limit: 10, pages: 0 },
        adminMarketplaceBookingStats: null,
        isBookingLoading: false,
        adminBookingDetails: null,
        config: {
            gst_rates: [],
            company_types: [],
            serviceability_options: [],
            billing_options: [],
            states: [],
            price_bounds: { min: 0, max: 100000 }
        },
        requirements: [],
        requirementPagination: { total: 0, page: 1, limit: 10, pages: 0 },
        requirementStats: { total: 0, open: 0, closed: 0 },
        requirementDetails: null,
        isLoading: false,
        isInitialLoad: true,
        error: null,
    },
    reducers: {
        clearListingDetails: (state) => {
            state.details = null;
        },
        clearRequirementDetails: (state) => {
            state.requirementDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Listings
            .addCase(fetchMarketplaceListings.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchMarketplaceListings.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isInitialLoad = false;
                state.listings = action.payload.data?.data || [];
                state.pagination = action.payload.data?.pagination || {};
            })
            .addCase(fetchMarketplaceListings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Stats
            .addCase(fetchMarketplaceStats.fulfilled, (state, action) => {
                state.stats = action.payload.data?.data || action.payload.data;
            })
            // Categories
            .addCase(fetchMarketplaceCategories.fulfilled, (state, action) => {
                state.categories = action.payload.data;
            })
            // Config
            .addCase(fetchMarketplaceConfig.fulfilled, (state, action) => {
                state.config = action.payload.data;
            })
            // Individual Detail
            .addCase(fetchListingDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchListingDetails.fulfilled, (state, action) => {
                state.details = action.payload.data;
                state.isLoading = false;
            })
            .addCase(fetchListingDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Create Bulk Offer
            .addCase(createBulkOffer.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            // Fetch Bulk Offer Requests
            .addCase(fetchBulkOfferRequests.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchBulkOfferRequests.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isInitialLoad = false;
                state.bulkOfferRequests = action.payload.data?.data || action.payload.data || [];
                state.bulkOfferRequestPagination = action.payload.data?.pagination || action.payload.data?.meta || action.payload.meta || {};
            })
            .addCase(fetchBulkOfferRequests.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Bulk Offer Request Stats
            .addCase(fetchBulkOfferRequestStats.fulfilled, (state, action) => {
                state.bulkOfferRequestStats = action.payload.data?.data || action.payload.data;
            })
            .addCase(deleteBulkOfferRequest.fulfilled, (state, action) => {
                state.bulkOfferRequests = state.bulkOfferRequests.filter(req => req.id !== action.payload);
            })

            // Fetch Admin Bookings
            .addCase(fetchAdminMarketplaceBookings.pending, (state) => {
                state.isBookingLoading = true;
            })
            .addCase(fetchAdminMarketplaceBookings.fulfilled, (state, action) => {
                state.isBookingLoading = false;
                state.adminMarketplaceBookings = action.payload.data;
                state.adminMarketplaceBookingPagination = action.payload.meta;
                state.isInitialLoad = false;
            })
            .addCase(fetchAdminMarketplaceBookings.rejected, (state, action) => {
                state.isBookingLoading = false;
                state.error = action.payload;
            })
            // Fetch Admin Booking Details
            .addCase(fetchAdminBookingDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAdminBookingDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.adminBookingDetails = action.payload.data;
            })
            .addCase(fetchAdminBookingDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch Admin Booking Stats
            .addCase(fetchAdminBookingStats.fulfilled, (state, action) => {
                state.adminMarketplaceBookingStats = action.payload.data;
            })
            // Toggle Active Status
            .addCase(toggleListingActiveStatus.fulfilled, (state, action) => {
                const { id, is_active } = action.payload;
                const index = state.listings.findIndex(item => item.id === id);
                if (index !== -1) {
                    state.listings[index].is_active = is_active;
                }
            })
            // Create User Listing
            .addCase(createUserListing.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createUserListing.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(createUserListing.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Update User Listing
            .addCase(updateUserListing.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateUserListing.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(updateUserListing.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Delete User Listing
            .addCase(deleteUserListing.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteUserListing.fulfilled, (state, action) => {
                state.isLoading = false;
                state.listings = state.listings.filter(listing => listing.id !== action.payload);
            })
            .addCase(deleteUserListing.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch Requirements
            .addCase(fetchMarketplaceRequirements.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchMarketplaceRequirements.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isInitialLoad = false;
                state.requirements = action.payload.data;
                state.requirementPagination = action.payload.meta || { total: 0, page: 1, limit: 10, pages: 0 };
            })
            .addCase(fetchMarketplaceRequirements.rejected, (state, action) => {
                state.isLoading = false;
                state.isInitialLoad = false;
                state.error = action.payload;
            })
            // Fetch Requirement Details
            .addCase(fetchMarketplaceRequirementDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchMarketplaceRequirementDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.requirementDetails = action.payload.data;
            })
            .addCase(fetchMarketplaceRequirementDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Create Requirement
            .addCase(createMarketplaceRequirement.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createMarketplaceRequirement.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(createMarketplaceRequirement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Update Requirement
            .addCase(updateMarketplaceRequirement.pending, (state) => {
                // Do not set isLoading = true to prevent list skeleton flashing during updates
            })
            .addCase(updateMarketplaceRequirement.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedRequirement = action.payload?.data;
                if (updatedRequirement && updatedRequirement.id) {
                    const index = state.requirements.findIndex(req => req.id === updatedRequirement.id);
                    if (index !== -1) {
                        state.requirements[index] = {
                            ...state.requirements[index],
                            ...updatedRequirement
                        };
                    }
                }
            })
            .addCase(updateMarketplaceRequirement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Delete Requirement
            .addCase(deleteMarketplaceRequirement.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteMarketplaceRequirement.fulfilled, (state, action) => {
                state.isLoading = false;
                state.requirements = state.requirements.filter(req => req.id !== action.payload.id);
            })
            .addCase(deleteMarketplaceRequirement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch Requirement Stats
            .addCase(fetchMarketplaceRequirementStats.fulfilled, (state, action) => {
                state.requirementStats = action.payload.data || { total: 0, open: 0, closed: 0 };
            });
    }
});

export const { clearListingDetails, clearRequirementDetails } = marketplaceSlice.actions;
export default marketplaceSlice.reducer;
